import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const SUBMIT_URL = 'https://readdy.ai/api/form/d6unhmmdp0k8rarn7ne0';

export default function FinalCTASection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="estimate" className="py-28 bg-brand-orange relative overflow-hidden">
      {/* Decorative circles */}
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full border-4 border-white/10 translate-x-1/3 -translate-y-1/3 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full border-4 border-white/10 -translate-x-1/3 translate-y-1/3 pointer-events-none"></div>

      <div className="relative max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-block w-16 h-1 bg-white/40 rounded-full mb-6"></div>
          <h2 className="font-heading font-900 text-white text-5xl md:text-6xl lg:text-7xl uppercase tracking-tight leading-none mb-4">
            Get Your Free Roofing<br />Estimate Today
          </h2>
          <p className="font-body text-white/80 text-lg max-w-xl mx-auto">
            No obligation. No pressure. Just honest expert advice from Utah&apos;s most trusted roofing contractor.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl p-8 md:p-10 max-w-3xl mx-auto">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-green-600 text-4xl"></i>
              </div>
              <h3 className="font-heading font-900 text-charcoal text-2xl uppercase tracking-wide mb-2">Request Received!</h3>
              <p className="font-body text-charcoal/60 text-base">
                Our team will contact you within 24 hours to schedule your free roof inspection.
              </p>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit}>
              <h3 className="font-heading font-900 text-charcoal text-2xl uppercase tracking-wide mb-6 text-center">
                Schedule Your Free Inspection
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="font-body text-charcoal font-600 text-xs uppercase tracking-widest block mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Smith"
                    className="w-full bg-surface border border-surface-dark px-4 py-3.5 rounded-lg font-body text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange"
                  />
                </div>
                <div>
                  <label className="font-body text-charcoal font-600 text-xs uppercase tracking-widest block mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="(801) 555-0199"
                    className="w-full bg-surface border border-surface-dark px-4 py-3.5 rounded-lg font-body text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange"
                  />
                </div>
                <div>
                  <label className="font-body text-charcoal font-600 text-xs uppercase tracking-widest block mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full bg-surface border border-surface-dark px-4 py-3.5 rounded-lg font-body text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange"
                  />
                </div>
                <div>
                  <label className="font-body text-charcoal font-600 text-xs uppercase tracking-widest block mb-2">
                    Service Needed *
                  </label>
                  <select
                    name="service"
                    required
                    className="w-full bg-surface border border-surface-dark px-4 py-3.5 rounded-lg font-body text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange cursor-pointer"
                  >
                    <option value="">Select a service...</option>
                    <option>Roof Replacement</option>
                    <option>Roof Repair</option>
                    <option>Commercial Roofing</option>
                    <option>Storm Damage Inspection</option>
                    <option>Insurance Claim Assistance</option>
                    <option>Roof Inspection</option>
                  </select>
                </div>
              </div>
              <div className="mb-5">
                <label className="font-body text-charcoal font-600 text-xs uppercase tracking-widest block mb-2">
                  Additional Details
                </label>
                <textarea
                  name="notes"
                  rows={3}
                  maxLength={500}
                  placeholder="Tell us about your roofing situation or any specific concerns..."
                  className="w-full bg-surface border border-surface-dark px-4 py-3.5 rounded-lg font-body text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange resize-none"
                />
              </div>
              {status === 'error' && (
                <p className="text-red-500 font-body text-sm mb-4">
                  Something went wrong. Please try again or call us directly.
                </p>
              )}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white font-heading font-900 text-xl uppercase tracking-widest py-4 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'submitting' ? 'Sending...' : 'Submit — Get My Free Estimate'}
              </button>
            </form>
          )}
        </div>

        {/* Alt CTA */}
        <div className="text-center mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="tel:+18015550199"
            className="flex items-center gap-2 text-white font-body font-700 text-lg cursor-pointer hover:text-white/80 transition-colors"
          >
            <i className="ri-phone-fill text-xl"></i>
            Or call: (801) 555-0199
          </a>
          <span className="text-white/40 hidden sm:block">|</span>
          <Link
            to="/services/roof-replacement"
            className="text-white/70 font-body text-sm hover:text-white transition-colors cursor-pointer"
          >
            Browse our services first &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}
